import { Avatar } from "@components/ui/Avatar"
import { Card, CardBody } from "@components/ui/Card"
import { EmptyState } from "@components/ui/EmptyState"
import { useGradeScale } from "@hooks/useGradeScale"
import { attentionCount } from "@lib/attention"
import { cn } from "@lib/cn"
import { formatGrade } from "@lib/intl"
import { ArrowRight, ChevronRight, Smile } from "lucide-react"
import { memo } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { gradeTier, gradeTierTextClass } from "@/constants/gradeTier"
import type { GradeScale, HomeAtRiskRow } from "@/types"

interface Props {
  rows: HomeAtRiskRow[]
  onStudentClick?: (studentId: string, classroomId: string) => void
}

// Individual student avg → keep the historical "student" tier (anchored
// on the 6.0 Mexican passing mark) so an at-risk student showing 6.2
// reads amber, not red. Scale-aware via the active professor setting.
const gradeColor = (avg: number, scale: GradeScale) =>
  gradeTierTextClass(gradeTier(avg, scale, "student"))

export const HomeAtRiskList = memo(function HomeAtRiskList({ rows, onStudentClick }: Props) {
  const { t } = useTranslation("home")
  const scale = useGradeScale()

  return (
    <Card>
      <CardBody className="space-y-3">
        <h2 className="text-ink text-sm font-semibold">{t("at_risk.title")}</h2>
        {rows.length === 0 ? (
          <EmptyState
            icon={<Smile className="text-success h-8 w-8" />}
            title={t("at_risk.empty.title")}
            message={t("at_risk.empty.message")}
          />
        ) : (
          <>
            <ul className="divide-hairline divide-y">
              {rows.map((row) => (
                <li key={row.profile_id ?? row.student_id}>
                  <Link
                    // PRD-40 — one row per PERSON now, so the link goes to
                    // their page. `materia` carries the worst subject so the
                    // professor lands looking at the one that needs them.
                    // Rows still awaiting the backfill keep the legacy URL,
                    // which redirects when it can.
                    to={
                      row.profile_id
                        ? `/alumnos/${row.profile_id}?materia=${row.classroom_id}`
                        : `/salones/${row.classroom_id}/alumnos/${row.student_id}`
                    }
                    onClick={() => onStudentClick?.(row.student_id, row.classroom_id)}
                    className="rounded-control hover:bg-inset focus-visible:ring-accent flex items-center gap-3 p-2 transition-colors focus:outline-none focus-visible:ring-2"
                  >
                    <Avatar name={row.name} color={row.color} initials={row.initials} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="text-ink truncate text-sm font-medium">{row.name}</p>
                      {/* Every subject they're at risk in, not just the
                          one the row links to — the professor needs to see
                          that it's two subjects, not one. */}
                      <p className="text-ink-subtle truncate text-xs">
                        {row.at_risk_subjects?.length
                          ? row.at_risk_subjects.join(" · ")
                          : row.classroom_name}
                      </p>
                      {/* PRD-58 §FR-5.3 — the reason in words, so the row
                          explains itself without the colour. */}
                      {row.attention_reason && (
                        <p className="text-danger-ink truncate text-xs">
                          {t(`at_risk.reason.${row.attention_reason}`, {
                            count: attentionCount(row.attention_reason, row.attention_n),
                          })}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "text-sm font-semibold tabular-nums",
                          gradeColor(row.avg_grade, scale),
                        )}
                      >
                        {row.avg_grade > 0 ? formatGrade(row.avg_grade, scale) : t("dash")}
                      </span>
                      <ChevronRight className="text-ink-subtle h-4 w-4" />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/salones"
              className="text-accent-ink inline-flex items-center gap-1 text-sm font-medium hover:underline"
            >
              {t("at_risk.view_all")} <ArrowRight className="h-3 w-3" />
            </Link>
          </>
        )}
      </CardBody>
    </Card>
  )
})
